import React, { useEffect, useState } from "react";
import {
  getJvvnlGssLoad,
  getJvvnlPredictionDateRange,
  getJvvnlSubstationPrediction,
  getJvvnlWeatherByLocation,
} from "../../../Rest_api/RestApi";
import { JvvnlGssLoadRow } from "../../../models/jvvnl";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../models/chart_model";
import { ChartTable } from "../../../components/table/ChartTable";
import "../../../components/styles.css";
import "./ForecastPrediction.css";
import { ChartAreaLoader } from "../../../components/Loading";
import MainPageContainer from "../../../components/MainPageContainer";
import Tooltip from "../../../components/tooltip";
import { SearchBox } from "../../../components/Search";
import { DynamicDatePicker } from "../../../components/DatePicker";
import { UploadCustomSeries, UploadedSeries } from "./UploadCustomSeries";
import { FilterTab } from "../../../components/Filters";
import MultiDateRangePicker, {
  DateRange,
} from "../../../components/MultiDateRangePicker";

type Option = {
  value: string | number;
  label: string;
  id?: number;
  locationId?: number;
};
type GroupedOption = { label: string; options: Option[] };

const ENTITY_OPTIONS: GroupedOption[] = [
  {
    label: "Discom",
    options: [
      { value: "jaipur_discom", label: "Jaipur", id: 1, locationId: 1 },
    ],
  },
  {
    label: "Substations",
    options: [
      { value: "BANSUR", label: "BANSUR", id: 4, locationId: 1 },
      { value: "BEHROR", label: "BEHROR", id: 5, locationId: 1 },
      { value: "KOTPUTLI", label: "KOTPUTLI", id: 3, locationId: 1 },
      { value: "NIMRANA", label: "NIMRANA", id: 43, locationId: 1 },
    ],
  },
];

const ALL_ENTITY_OPTIONS: Option[] = ENTITY_OPTIONS.flatMap((g) => g.options);

const WEATHER_PARAMS = [
  {
    key: "temperature_2m",
    label: "Temperature (°C)",
    chartLabel: "🌡️ Temperature (°C)",
    unit: "°C",
  },
  {
    key: "relative_humidity_2m",
    label: "Humidity (%)",
    chartLabel: "💧 Humidity (%)",
    unit: "%",
  },
  {
    key: "apparent_temperature",
    label: "Apparent Temp (°C)",
    chartLabel: "🌡️ Apparent Temp (°C)",
    unit: "°C",
  },
  {
    key: "precipitation",
    label: "Precipitation (mm)",
    chartLabel: "🌧️ Precipitation (mm)",
    unit: "mm",
  },
  { key: "rain", label: "Rain (mm)", chartLabel: "🌧️ Rain (mm)", unit: "mm" },
  {
    key: "cloud_cover",
    label: "Cloud Cover (%)",
    chartLabel: "☁️ Cloud Cover (%)",
    unit: "%",
  },
  {
    key: "wind_speed_80m",
    label: "Wind 80m (km/h)",
    chartLabel: "💨 Wind 80m (km/h)",
    unit: "km/h",
  },
  {
    key: "sunshine_duration",
    label: "Sunshine Duration (s)",
    chartLabel: "☀️ Sunshine Duration (s)",
    unit: "s",
  },
  {
    key: "direct_radiation",
    label: "Direct Radiation (W/m²)",
    chartLabel: "🔆 Direct Radiation (W/m²)",
    unit: "W/m²",
  },
];

const WEATHER_VARIABLES: Option[] = WEATHER_PARAMS.map((w) => ({
  value: w.key,
  label: w.label,
}));

const ForecastPrediction = () => {
  const [loading, setLoading] = useState(false);

  const [forecastDate, setForecastDate] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [historicalRanges, setHistoricalRanges] = useState<DateRange[]>([]);
  const [weatherRanges, setWeatherRanges] = useState<DateRange[]>([]);
  const [weatherVariables, setWeatherVariables] = useState<Option[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Option | null>(
    ALL_ENTITY_OPTIONS[0],
  );
  const [uploadedSeries, setUploadedSeries] = useState<UploadedSeries[]>([]);
  const [activeView, setActiveView] = useState("Chart");
  const VIEW_TABS = ["Chart", "Table"];

  const [dateBounds, setDateBounds] = useState<{
    minDate: string;
    maxDate: string;
  }>({ minDate: "2021-01-01", maxDate: "2028-12-31" });

  const [modelId, setModelId] = useState<number | null>(null);
  const [chartData, setChartData] = useState<
    Array<{ slot: number; slotLabel: string; [key: string]: number | string }>
  >([]);
  const [chartLegendName, setChartLegendName] = useState<string>("");
  const [chartLoading, setChartLoading] = useState(false);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [historicalRows, setHistoricalRows] = useState<JvvnlGssLoadRow[]>([]);
  const [historicalLegends, setHistoricalLegends] = useState<string[]>([]);
  const [originalForecastByslot, setOriginalForecastByslot] = useState<
    Array<{ slot: number; slotLabel: string; [k: string]: number | string }>
  >([]);
  const [forecastByslot, setForecastByslot] = useState<
    Array<{ slot: number; slotLabel: string; [k: string]: number | string }>
  >([]);
  const [weatherRows, setWeatherRows] = useState<
    Array<{
      date: string;
      time_block: number;
      [k: string]: number | string | null;
    }>
  >([]);
  const [weatherLegends, setWeatherLegends] = useState<
    Array<{ dataKey: string; name: string; unit: string }>
  >([]);
  const [uploadLegends, setUploadLegends] = useState<
    Array<{ dataKey: string; name: string }>
  >([]);

  const formatYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    const subId = selectedEntity?.id;
    if (!subId) return;
    let cancelled = false;
    (async () => {
      const {
        minDate,
        maxDate,
        modelId: mId,
      } = await getJvvnlPredictionDateRange({
        substationId: subId,
        startDate: "2025-12-01",
        endDate: "2026-01-01",
      });
      if (cancelled) return;
      const newMin = minDate ?? "2021-01-01";
      const newMax = maxDate ?? "2028-12-31";
      setDateBounds({ minDate: newMin, maxDate: newMax });
      setModelId(mId);
      setForecastDate((prev) => {
        const minMs = new Date(newMin).getTime();
        const maxMs = new Date(newMax).getTime();
        const curMs = prev.startDate.getTime();
        if (curMs < minMs || curMs > maxMs) {
          const clamped = new Date(newMax);
          return { ...prev, startDate: clamped, endDate: clamped };
        }
        return prev;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEntity?.id]);

  useEffect(() => {
    const subId = selectedEntity?.id;
    if (!subId || !modelId || !forecastDate.startDate) {
      setForecastByslot([]);
      setOriginalForecastByslot([]);
      setChartLegendName("");
      setChartLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setChartLoading(true);
      try {
        const dateStr = formatYmd(forecastDate.startDate);
        const forecastLegend = `${dateStr} Forecasted`;
        const { predictions } = await getJvvnlSubstationPrediction({
          substationId: subId,
          startDate: dateStr,
          endDate: dateStr,
          modelId,
        });
        if (cancelled) return;
        const rows = predictions
          .slice()
          .sort((a, b) => a.slot - b.slot)
          .map((p) => {
            const startMin = (p.slot - 1) * 15;
            const endMin = p.slot * 15;
            const fmt = (mins: number) => {
              const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
              const m = String(mins % 60).padStart(2, "0");
              return `${h}:${m}`;
            };
            const slotLabel = `${fmt(startMin)}-${fmt(endMin)}`;
            return {
              slot: p.slot,
              slotLabel,
              [forecastLegend]: p.predicted_mw,
            };
          });
        setOriginalForecastByslot(rows as any);
        setForecastByslot(rows as any);
        setChartLegendName(forecastLegend);
      } catch (error) {
        if (cancelled) return;
        setOriginalForecastByslot([]);
        setForecastByslot([]);
        setChartLegendName("");
      } finally {
        if (!cancelled) {
          setChartLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEntity?.id, modelId, forecastDate.startDate]);

  useEffect(() => {
    const subId = selectedEntity?.id;
    if (!subId || historicalRanges.length === 0) {
      setHistoricalRows([]);
      setHistoricalLoading(false);
      return;
    }
    let cancelled = false;
    setHistoricalLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          historicalRanges.map((r) =>
            getJvvnlGssLoad({
              startDate: formatYmd(r.startDate),
              endDate: formatYmd(r.endDate),
              stationId: subId,
            }).catch(() => ({ rows: [] as JvvnlGssLoadRow[] })),
          ),
        );
        if (cancelled) return;
        setHistoricalRows(results.flatMap((r) => r.rows));
      } finally {
        if (!cancelled) {
          setHistoricalLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEntity?.id, historicalRanges]);

  useEffect(() => {
    const locId = selectedEntity?.locationId;
    if (!locId || weatherRanges.length === 0 || weatherVariables.length === 0) {
      setWeatherRows([]);
      setWeatherLoading(false);
      return;
    }
    let cancelled = false;
    setWeatherLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          weatherRanges.map((r) =>
            getJvvnlWeatherByLocation({
              startDate: formatYmd(r.startDate),
              endDate: formatYmd(r.endDate),
              locationId: locId,
            }).catch(() => []),
          ),
        );
        if (cancelled) return;
        setWeatherRows(results.flat() as any);
      } finally {
        if (!cancelled) {
          setWeatherLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEntity?.locationId, weatherRanges, weatherVariables]);

  useEffect(() => {
    const slotMap = new Map<
      number,
      { slot: number; slotLabel: string; [k: string]: number | string }
    >();
    for (const row of forecastByslot) {
      slotMap.set(row.slot, { ...row });
    }
    const histLegendSet = new Set<string>();
    for (const r of historicalRows) {
      const legend = r.date;
      histLegendSet.add(legend);
      const slot = r.block;
      let entry = slotMap.get(slot);
      if (!entry) {
        const startMin = (slot - 1) * 15;
        const endMin = slot * 15;
        const fmt = (mins: number) => {
          const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
          const m = String(mins % 60).padStart(2, "0");
          return `${h}:${m}`;
        };
        entry = { slot, slotLabel: `${fmt(startMin)}-${fmt(endMin)}` };
        slotMap.set(slot, entry);
      }
      entry[legend] = r.value_mw;
    }
    const wLegendMap = new Map<string, { name: string; unit: string }>();
    const selectedWeatherKeys = weatherVariables.map((v) => String(v.value));
    for (const w of weatherRows) {
      const slot = w.time_block;
      let entry = slotMap.get(slot);
      if (!entry) {
        const startMin = (slot - 1) * 15;
        const endMin = slot * 15;
        const fmt = (mins: number) => {
          const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
          const m = String(mins % 60).padStart(2, "0");
          return `${h}:${m}`;
        };
        entry = { slot, slotLabel: `${fmt(startMin)}-${fmt(endMin)}` };
        slotMap.set(slot, entry);
      }
      for (const k of selectedWeatherKeys) {
        const val = w[k];
        if (val === null || val === undefined) continue;
        const dataKey = `weather_${w.date}_${k}`;
        const wp = WEATHER_PARAMS.find((p) => p.key === k);
        const name = `${w.date} ${wp?.chartLabel ?? k}`;
        wLegendMap.set(dataKey, { name, unit: wp?.unit ?? "" });
        const cur = entry[dataKey];
        if (typeof val === "number") {
          if (typeof cur === "number") {
            entry[dataKey] = (cur + val) / 2;
          } else {
            entry[dataKey] = val;
          }
        }
      }
    }
    const uLegends: Array<{ dataKey: string; name: string }> = [];
    uploadedSeries.forEach((series, idx) => {
      if (series.visible === false) return;
      const dataKey = `upload_${series.id}`;
      const name = `Upload ${idx + 1}`;
      uLegends.push({ dataKey, name });
      for (const r of series.rows) {
        const slot = r.block;
        let entry = slotMap.get(slot);
        if (!entry) {
          const startMin = (slot - 1) * 15;
          const endMin = slot * 15;
          const fmt = (mins: number) => {
            const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
            const m = String(mins % 60).padStart(2, "0");
            return `${h}:${m}`;
          };
          entry = { slot, slotLabel: `${fmt(startMin)}-${fmt(endMin)}` };
          slotMap.set(slot, entry);
        }
        if (typeof r.forecast_mw === "number" && !isNaN(r.forecast_mw)) {
          entry[dataKey] = r.forecast_mw;
        }
      }
    });
    const merged = Array.from(slotMap.values()).sort((a, b) => a.slot - b.slot);
    setChartData(merged);
    setHistoricalLegends(Array.from(histLegendSet).sort());
    setWeatherLegends(
      Array.from(wLegendMap.entries()).map(([dataKey, v]) => ({
        dataKey,
        name: v.name,
        unit: v.unit,
      })),
    );
    setUploadLegends(uLegends);
  }, [
    forecastByslot,
    historicalRows,
    weatherRows,
    weatherVariables,
    uploadedSeries,
  ]);

  const combinedLegends = React.useMemo(() => {
    const out: Array<{
      name: string;
      dataKey: string;
      type: ChartType;
      yAxisId?: string;
      unit?: string;
    }> = [];
    for (const d of historicalLegends) {
      out.push({
        name: d,
        dataKey: d,
        type: ChartType.Line,
        opacity: 0.35,
      } as any);
    }
    for (const u of uploadLegends) {
      out.push({
        name: u.name,
        dataKey: u.dataKey,
        type: ChartType.Line,
        opacity: 0.35,
      } as any);
    }
    for (const w of weatherLegends) {
      out.push({
        name: w.name,
        dataKey: w.dataKey,
        type: ChartType.Line,
        yAxisId: "right",
        unit: w.unit,
        opacity: 0.35,
      } as any);
    }
    if (chartLegendName && forecastByslot.length > 0) {
      out.push({
        name: chartLegendName,
        dataKey: chartLegendName,
        type: ChartType.Line,
        smooth: false,
        lineWidth: 2.6,
        opacity: 1,
        legendColor: "#E17A3A",
      } as any);
    }
    return out;
  }, [
    chartLegendName,
    forecastByslot.length,
    historicalLegends,
    weatherLegends,
    uploadLegends,
  ]);

  const updateForecastSlotValue = React.useCallback(
    ({
      slotValue,
      row,
      newValue,
    }: {
      slotValue: unknown;
      row?: { [k: string]: number | string | Date | null | undefined };
      newValue: number;
    }) => {
      if (!chartLegendName) return;
      const slot = Number(slotValue);
      const slotLabel =
        row && typeof row.slotLabel === "string" ? row.slotLabel : undefined;
      if (!Number.isFinite(slot) && !slotLabel) return;

      setForecastByslot((prev) => {
        let changed = false;
        const next = prev.map((item) => {
          const isSlotMatch =
            Number.isFinite(slot) && Number(item.slot) === slot;
          const isSlotLabelMatch =
            !isSlotMatch &&
            !!slotLabel &&
            String(item.slotLabel) === String(slotLabel);
          if (!isSlotMatch && !isSlotLabelMatch) return item;
          const current = Number(item[chartLegendName]);
          if (
            Number.isFinite(current) &&
            Math.abs(current - newValue) < 0.000001
          ) {
            return item;
          }
          changed = true;
          return { ...item, [chartLegendName]: newValue };
        });
        return changed ? next : prev;
      });

      // Keep table/chart rows in sync immediately while derived merge effect runs.
      setChartData((prev) =>
        prev.map((item) =>
          (Number.isFinite(slot) && Number(item.slot) === slot) ||
          (!!slotLabel && String(item.slotLabel) === String(slotLabel))
            ? { ...item, [chartLegendName]: newValue }
            : item,
        ),
      );
    },
    [chartLegendName],
  );

  const handleForecastPointChange = React.useCallback(
    ({
      row,
      newValue,
    }: {
      row: { [k: string]: number | string | Date | null | undefined };
      newValue: number;
    }) => {
      updateForecastSlotValue({
        slotValue: row.slot,
        row,
        newValue,
      });
    },
    [updateForecastSlotValue],
  );

  const editableForecastSeries = React.useMemo(() => {
    if (!chartLegendName || forecastByslot.length === 0) return undefined;
    return {
      enabled: activeView === "Chart",
      dataKey: chartLegendName,
      min: 0,
      step: 0.01,
      handleColor: "#E17A3A",
      handleSize: 4.5,
      onPointChange: handleForecastPointChange,
    };
  }, [
    activeView,
    chartLegendName,
    forecastByslot.length,
    handleForecastPointChange,
  ]);

  const hasForecastAdjustments = React.useMemo(() => {
    if (!chartLegendName || originalForecastByslot.length === 0) return false;
    const originalBySlot = new Map(
      originalForecastByslot.map((row) => [
        row.slot,
        Number(row[chartLegendName]),
      ]),
    );
    return forecastByslot.some((row) => {
      const originalValue = originalBySlot.get(row.slot);
      const currentValue = Number(row[chartLegendName]);
      return (
        Number.isFinite(originalValue) &&
        Number.isFinite(currentValue) &&
        Math.abs((originalValue as number) - currentValue) > 0.000001
      );
    });
  }, [chartLegendName, forecastByslot, originalForecastByslot]);

  const resetForecastAdjustments = () => {
    setForecastByslot(originalForecastByslot.map((row) => ({ ...row })));
  };

  const downloadAdjustedForecast = () => {
    if (!chartLegendName || forecastByslot.length === 0) return;
    const dateStr = formatYmd(forecastDate.startDate);
    const entity = selectedEntity?.label ?? "ENTITY";
    const escapeCsv = (value: string | number) => {
      const text = String(value);
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const header = "entity,date,block,time,forecast_mw";
    const rows = forecastByslot
      .slice()
      .sort((a, b) => a.slot - b.slot)
      .map((row) => {
        const forecastValue = Number(row[chartLegendName]);
        return [
          entity,
          dateStr,
          row.slot,
          row.slotLabel,
          Number.isFinite(forecastValue) ? forecastValue.toFixed(2) : "",
        ]
          .map(escapeCsv)
          .join(",");
      });
    const blob = new Blob([[header, ...rows].join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `adjusted_forecast_${entity}_${dateStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="gas-page-container">
        <MainPageContainer className="!w-[calc(100vw)]">
          <div style={{ height: "calc(100vh - 100px)" }}>
            {loading ? (
              <ChartAreaLoader />
            ) : (
              <>
                {/* ── Top Bar ── */}
                <div className="fp-topbar">
                  <div className="fp-topbar-left">
                    <span className="fp-title">Forecast Prediction</span>
                    <div className="fp-divider-v" />
                    {/* Entity pill */}
                    <div className="fp-entity-pill">
                      <div className="fp-entity-dot" />
                      <div className="pill-select">
                        <SearchBox
                          options={ENTITY_OPTIONS as any}
                          selectedOptions={
                            selectedEntity ? [selectedEntity] : []
                          }
                          onChange={(selected) =>
                            setSelectedEntity(selected as Option | null)
                          }
                          placeholder="Select Entity"
                          isMany={false}
                          minWidth={130}
                          maxWidth={180}
                        />
                      </div>
                    </div>
                  </div>
                  <div />
                </div>

                {/* ── Filter Strip ── */}
                <div className="fp-filter-strip">
                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Date</span>
                    <DynamicDatePicker
                      defaultDates={forecastDate}
                      setDate={(dates) => {
                        const range = dates[0] ?? forecastDate;
                        setForecastDate({
                          ...range,
                          endDate: range.startDate,
                        });
                      }}
                      singleDate={true}
                      minDate={dateBounds.minDate}
                      maxDate={dateBounds.maxDate}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Historical</span>
                    <MultiDateRangePicker
                      singleDate={true}
                      selectedRanges={historicalRanges}
                      setDates={(ranges) => {
                        const normalized = ranges.map((r) => ({
                          ...r,
                          endDate: r.startDate,
                        }));
                        setHistoricalRanges(normalized);
                      }}
                      placeholder="Historical Dates"
                      minWidth={160}
                      maxWidth={240}
                      maxDate={(() => {
                        const y = new Date();
                        y.setDate(y.getDate() - 1);
                        return y.toISOString().split("T")[0];
                      })()}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Weather Dates</span>
                    <MultiDateRangePicker
                      singleDate={true}
                      selectedRanges={weatherRanges}
                      setDates={(ranges) => {
                        const normalized = ranges.map((r) => ({
                          ...r,
                          endDate: r.startDate,
                        }));
                        setWeatherRanges(normalized);
                      }}
                      placeholder="Weather Dates"
                      minWidth={160}
                      maxWidth={240}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Variables</span>
                    <SearchBox
                      options={WEATHER_VARIABLES}
                      selectedOptions={weatherVariables}
                      onChange={(selected) =>
                        setWeatherVariables(selected as Option[])
                      }
                      placeholder="Weather Variable"
                      isMany={true}
                      minWidth={160}
                      maxWidth={240}
                    />
                  </div>
                </div>

                {/* ── Chart Section ── */}
                <div className="fp-chart-section">
                  {/* Upload row */}
                  <div className="fp-upload-row">
                    <div className="fp-upload-left">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="#C55D1C"
                        strokeWidth="2"
                        className="fp-upload-icon"
                      >
                        <path d="M8 10V3M5 6l3-3 3 3" />
                        <path d="M3 13h10" />
                      </svg>
                      <div>
                        <div className="fp-upload-title">
                          Upload Custom Series
                        </div>
                        <div className="fp-upload-desc">
                          Download the 96-block template, fill the{" "}
                          <code>forecast_mw</code> column, then upload. Toggle
                          series on/off as overlays.
                        </div>
                        {uploadedSeries.length === 0 && (
                          <div className="fp-upload-empty">
                            No series uploaded.{" "}
                            <button
                              type="button"
                              className="fp-upload-empty-btn"
                              onClick={() => {
                                const today = new Date()
                                  .toISOString()
                                  .split("T")[0];
                                const entity =
                                  selectedEntity?.label ?? "ENTITY";
                                const header =
                                  "entity,date,block,time,forecast_mw";
                                const slots = Array.from(
                                  { length: 96 },
                                  (_, i) => {
                                    const h = String(
                                      Math.floor((i * 15) / 60),
                                    ).padStart(2, "0");
                                    const m = String((i * 15) % 60).padStart(
                                      2,
                                      "0",
                                    );
                                    return `${entity},${today},${i + 1},${h}:${m},`;
                                  },
                                );
                                const csv = [header, ...slots].join("\n");
                                const blob = new Blob([csv], {
                                  type: "text/csv",
                                });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `adjusted_forecast_${entity}_${today}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                            >
                              Download the template
                            </button>{" "}
                            to start.
                          </div>
                        )}
                        {uploadedSeries.length > 0 && (
                          <div className="fp-chips">
                            {uploadedSeries.map((s) => (
                              <span
                                key={s.id}
                                className={`fp-chip ${s.errors.length ? "fp-chip--err" : "fp-chip--ok"}`}
                              >
                                {s.fileName}
                                <button
                                  type="button"
                                  className="fp-chip-remove"
                                  onClick={() =>
                                    setUploadedSeries((prev) =>
                                      prev.filter((x) => x.id !== s.id),
                                    )
                                  }
                                >
                                  ✕
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="fp-upload-right">
                      <UploadCustomSeries
                        series={uploadedSeries}
                        onChange={setUploadedSeries}
                        defaultEntity={
                          selectedEntity
                            ? String(selectedEntity.label)
                            : undefined
                        }
                        buttonsOnly
                      />
                    </div>
                  </div>

                  {/* Chart/Table tab bar */}
                  <div className="fp-tab-bar">
                    <FilterTab
                      options={VIEW_TABS}
                      selected={[activeView]}
                      onClick={(option) => setActiveView(option)}
                      variant="pill-outline"
                    />
                  </div>

                  {/* Chart / Table area */}
                  <div className="fp-view-area">
                    {chartLoading || historicalLoading || weatherLoading ? (
                      <ChartAreaLoader />
                    ) : activeView === "Chart" ? (
                      <div className="fp-chart-placeholder">
                        <ReMixChart
                          data={chartData}
                          xDataKey="slot"
                          yAxisLabel="MW"
                          unit="MW"
                          chartContainerHeight="calc(100vh - 460px)"
                          onlyTitle={true}
                          isTimeSlot={true}
                          secondYAxisLabel={
                            weatherLegends.length > 0 ? "Weather" : undefined
                          }
                          legends={combinedLegends}
                          editableSeries={editableForecastSeries}
                        />
                      </div>
                    ) : (
                      <div className="fp-table-placeholder">
                        <ChartTable
                          tableHeight="calc(100vh - 440px)"
                          data={chartData}
                          legends={combinedLegends}
                          unit="MW"
                          extraKeys={["slot", "slotLabel"]}
                          extraLabels={["Block", "Time"]}
                          itemsPerPage={96}
                          defaultSortKey="slot"
                          defaultSortDirection="asc"
                          filename={`JVVNL_${chartLegendName || "substation"}_${formatYmd(forecastDate.startDate)}`}
                          editableColumn={
                            chartLegendName && forecastByslot.length > 0
                              ? {
                                  dataKey: chartLegendName,
                                  rowKey: "slot",
                                  min: 0,
                                  step: 0.01,
                                  onCellChange: ({
                                    rowKeyValue,
                                    row,
                                    newValue,
                                  }) =>
                                    updateForecastSlotValue({
                                      slotValue: rowKeyValue,
                                      row,
                                      newValue,
                                    }),
                                  originalByRowKey: Object.fromEntries(
                                    originalForecastByslot.map((r) => [
                                      r.slot,
                                      Number(r[chartLegendName]),
                                    ]),
                                  ),
                                  onRevert: (rowKeyValue) => {
                                    const slotNum = Number(rowKeyValue);
                                    const original =
                                      originalForecastByslot.find(
                                        (r) => r.slot === slotNum,
                                      );
                                    if (!original) return;
                                    updateForecastSlotValue({
                                      slotValue: slotNum,
                                      row: original,
                                      newValue: Number(
                                        original[chartLegendName],
                                      ),
                                    });
                                  },
                                }
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Bottom Bar ── */}
                <div className="fp-bottombar">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className="fp-bottombar-hint">
                      Drag forecast point to adjust · Click legend to hide/show
                      lines
                    </span>
                  </div>
                  <div className="fp-bottombar-actions">
                    <button
                      type="button"
                      className="fp-btn-reset"
                      onClick={resetForecastAdjustments}
                      disabled={!hasForecastAdjustments}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M2 8a6 6 0 1 0 .8-3M2 2v4h4" />
                      </svg>
                      Reset to Original
                    </button>
                    <button
                      type="button"
                      className="fp-btn-publish"
                      onClick={downloadAdjustedForecast}
                      disabled={forecastByslot.length === 0}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M8 10V3M5 7l3 3 3-3" />
                        <rect x="2" y="12" width="12" height="2" rx="1" />
                      </svg>
                      Publish / Download
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5">
              <Tooltip note={<p></p>} text="">
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default ForecastPrediction;
